/**
 * Origine de l'API — résolue à l'exécution, pas seulement à la compilation.
 *
 * Sans nom de domaine, l'API est exposée par un « tunnel rapide » Cloudflare
 * dont l'adresse (`xxx.trycloudflare.com`) CHANGE à chaque redémarrage. Si
 * cette adresse était figée dans le bundle, il faudrait reconstruire et
 * redéployer le site à chaque fois.
 *
 * Ordre de résolution :
 *   1. `?api=https://…` dans l'URL   → mémorisé sur l'appareil
 *   2. ce qui a été mémorisé
 *   3. VITE_API_URL, figé à la compilation
 *   4. rien : même origine (développement local, proxy Vite)
 *
 * En démonstration, il suffit d'ouvrir une fois
 *   https://friends.pages.dev/?api=https://xyz.trycloudflare.com
 * sur le téléphone, et l'appareil s'en souvient.
 */
const CLE = 'friends.api';

function resoudre(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    const fourni = params.get('api');
    if (fourni) {
      const propre = fourni.trim().replace(/\/+$/, '');
      if (/^https?:\/\//.test(propre)) {
        localStorage.setItem(CLE, propre);
        // On retire le paramètre de la barre d'adresse : il ne sert qu'une fois.
        params.delete('api');
        const reste = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (reste ? `?${reste}` : ''));
        return propre;
      }
    }
    const memorise = localStorage.getItem(CLE);
    if (memorise) return memorise;
  } catch { /* localStorage indisponible : on retombe sur la valeur de compilation */ }

  return (import.meta.env.VITE_API_URL ?? '').trim().replace(/\/+$/, '');
}

export const API_BASE = resoudre();

/** Change l'adresse de l'API et recharge — utilisé par l'écran de connexion. */
export function definirApi(url: string): void {
  const propre = url.trim().replace(/\/+$/, '');
  if (propre) localStorage.setItem(CLE, propre); else localStorage.removeItem(CLE);
  window.location.reload();
}

export const urlApi = (chemin: string): string =>
  chemin.startsWith('http') ? chemin : `${API_BASE}${chemin.startsWith('/') ? '' : '/'}${chemin}`;

/**
 * URL d'un document imprimable, ouvert dans un onglet séparé.
 *
 * Un `window.open` ne porte pas d'en-tête Authorization : le jeton passe en
 * paramètre d'URL, et le serveur le relit (voir app.ts, fonction `impression`).
 *
 * Ces routes-la sont exposees A LA RACINE du serveur, pas sous `/api` : l'URL
 * d'un contrat imprime se lit et se partage. Le proxy de developpement doit
 * donc les transmettre lui aussi — voir web/vite.config.ts.
 */
export const urlImpression = (chemin: string, jeton: string | null): string =>
  `${urlApi(chemin)}${chemin.includes('?') ? '&' : '?'}token=${encodeURIComponent(jeton ?? '')}`;
